import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const response = await fetch('https://readdy.ai/api/form/d6cn18qtehdqnvnpigb0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email }).toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const shopLinks = [
    { to: '/products', label: 'Toute la Collection' },
    { to: '/products?category=perfume', label: 'Parfums' },
    { to: '/products?category=hair-oil', label: 'Huiles Capillaires' },
    { to: '/products', label: 'Nouveautés' },
  ];

  const serviceLinks = [
    { to: '/about', label: 'Notre Maison' },
    { to: '/shipping-returns', label: 'Livraison & Retours' },
    { to: '/order-tracking', label: 'Suivi de Commande' },
    { to: '/about#faq', label: 'FAQ' },
    { to: '/blog', label: 'Journal' },
  ];

  const socialLinks = [
    { icon: 'ri-facebook-fill', href: 'https://facebook.com', label: 'Facebook' },
    { icon: 'ri-instagram-line', href: 'https://instagram.com', label: 'Instagram' },
    { icon: 'ri-twitter-x-line', href: 'https://twitter.com', label: 'Twitter' },
    { icon: 'ri-pinterest-line', href: 'https://pinterest.com', label: 'Pinterest' },
  ];

  return (
    <footer className="bg-[#0F2E1E] relative overflow-hidden">
      {/* Decorative top border */}
      <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D4A574]/50 to-transparent"></div>

      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4A574]/[0.02] rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#D4A574]/[0.02] rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

      {/* Newsletter Section */}
      <div className="relative border-b border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-14">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-5 sm:gap-8">
            <div className="text-center lg:text-left max-w-md">
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-2 sm:mb-3">
                <div className="w-6 h-[1px] bg-[#D4A574]/40"></div>
                <span className="text-[10px] uppercase tracking-[3px] text-[#D4A574]/60 font-medium">Exclusivités</span>
                <div className="w-6 h-[1px] bg-[#D4A574]/40"></div>
              </div>
              <h3 className="text-lg sm:text-xl font-serif text-white/90 mb-1.5 sm:mb-2 tracking-wide">
                Rejoignez le Cercle Daliah
              </h3>
              <p className="text-xs sm:text-sm text-white/35 leading-relaxed">
                Recevez en avant-première nos nouvelles créations, offres exclusives et conseils beauté.
              </p>
            </div>
            <div className="w-full lg:w-auto lg:min-w-[420px]">
              <form
                id="footer-newsletter-form"
                data-readdy-form
                onSubmit={handleNewsletterSubmit}
                className="flex flex-col sm:flex-row gap-2.5 sm:gap-2"
              >
                <div className="flex-1 relative">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Votre adresse email"
                    required
                    className="w-full px-4 sm:px-5 py-3 sm:py-3.5 text-sm bg-white/[0.04] border border-[#D4A574]/15 rounded-lg text-white placeholder:text-white/25 focus:outline-none focus:border-[#D4A574]/40 focus:bg-white/[0.06] transition-all duration-300 font-light tracking-wide"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-7 py-3 sm:py-3.5 bg-[#D4A574] text-[#0B2B1A] text-sm font-semibold rounded-lg hover:bg-[#E0B888] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 tracking-wide"
                >
                  {isSubmitting ? (
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      S&apos;inscrire
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                  )}
                </button>
              </form>
              {submitStatus === 'success' && (
                <p className="mt-2.5 sm:mt-3 text-xs sm:text-sm text-emerald-400/80 flex items-center gap-1.5">
                  <i className="ri-check-line text-base w-4 h-4 flex items-center justify-center"></i>
                  Bienvenue dans le Cercle Daliah !
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="mt-2.5 sm:mt-3 text-xs sm:text-sm text-red-400/80 flex items-center gap-1.5">
                  <i className="ri-error-warning-line text-base w-4 h-4 flex items-center justify-center"></i>
                  Une erreur est survenue. Réessayez.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-8 sm:pt-14 pb-8 sm:pb-10">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-8 mb-10 sm:mb-14">

          {/* Brand Column */}
          <div className="col-span-2 sm:col-span-2 lg:col-span-4">
            <Link to="/" className="inline-flex flex-col group mb-4 sm:mb-6">
              <span className="text-[8px] uppercase tracking-[3px] text-[#D4A574]/40 font-medium mb-0.5">Maison</span>
              <span className="text-xl sm:text-[22px] font-semibold text-white font-serif tracking-wider group-hover:text-[#D4A574] transition-colors duration-500">
                Daliah Secret
              </span>
            </Link>
            <p className="text-white/30 text-xs sm:text-sm leading-relaxed mb-5 sm:mb-7 max-w-xs font-light">
              Créations olfactives d&apos;exception et soins capillaires précieux, formulés avec les plus nobles ingrédients naturels pour sublimer votre beauté.
            </p>
            <div className="flex items-center gap-2.5 sm:gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full border border-white/[0.06] text-white/25 hover:border-[#D4A574]/30 hover:text-[#D4A574] hover:bg-[#D4A574]/5 transition-all duration-300 cursor-pointer"
                >
                  <i className={`${social.icon} text-sm sm:text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Shop Links */}
          <div className="col-span-1 lg:col-span-2">
            <h4 className="text-[10px] uppercase tracking-[2.5px] text-[#D4A574]/50 font-semibold mb-4 sm:mb-6">
              Boutique
            </h4>
            <ul className="space-y-2.5 sm:space-y-3.5">
              {shopLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="text-white/35 hover:text-[#D4A574] text-xs sm:text-sm transition-colors duration-300 cursor-pointer font-light tracking-wide"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Links */}
          <div className="col-span-1 lg:col-span-2">
            <h4 className="text-[10px] uppercase tracking-[2.5px] text-[#D4A574]/50 font-semibold mb-4 sm:mb-6">
              Assistance
            </h4>
            <ul className="space-y-2.5 sm:space-y-3.5">
              {serviceLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-white/35 hover:text-[#D4A574] text-xs sm:text-sm transition-colors duration-300 cursor-pointer font-light tracking-wide"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Trust */}
          <div className="col-span-2 sm:col-span-2 lg:col-span-4">
            <h4 className="text-[10px] uppercase tracking-[2.5px] text-[#D4A574]/50 font-semibold mb-4 sm:mb-6">
              Contact
            </h4>
            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              <a 
                href="mailto:contact@daliahsecret.com"
                className="flex items-center gap-2.5 sm:gap-3 group cursor-pointer"
              >
                <i className="ri-mail-line text-[#D4A574]/40 group-hover:text-[#D4A574] text-sm sm:text-base w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
                <span className="text-white/35 group-hover:text-[#D4A574] text-xs sm:text-sm font-light transition-colors duration-300">contact@daliahsecret.com</span>
              </a>
              <a 
                href="tel:+13023997281"
                className="flex items-center gap-2.5 sm:gap-3 group cursor-pointer"
              >
                <i className="ri-phone-line text-[#D4A574]/40 group-hover:text-[#D4A574] text-sm sm:text-base w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
                <span className="text-white/35 group-hover:text-[#D4A574] text-xs sm:text-sm font-light transition-colors duration-300">302 399 7281</span>
              </a>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div className="flex items-center gap-2 sm:gap-2.5 px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                <i className="ri-shield-check-line text-[#D4A574]/40 text-sm sm:text-base w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center shrink-0"></i>
                <span className="text-[9px] sm:text-[10px] text-white/25 tracking-wide font-light leading-tight">Paiement sécurisé</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-2.5 px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                <i className="ri-truck-line text-[#D4A574]/40 text-sm sm:text-base w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center shrink-0"></i>
                <span className="text-[9px] sm:text-[10px] text-white/25 tracking-wide font-light leading-tight">Livraison offerte</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-2.5 px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                <i className="ri-leaf-line text-[#D4A574]/40 text-sm sm:text-base w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center shrink-0"></i>
                <span className="text-[9px] sm:text-[10px] text-white/25 tracking-wide font-light leading-tight">100% Naturel</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-2.5 px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                <i className="ri-gift-2-line text-[#D4A574]/40 text-sm sm:text-base w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center shrink-0"></i>
                <span className="text-[9px] sm:text-[10px] text-white/25 tracking-wide font-light leading-tight">Échantillons offerts</span>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-[1px] bg-gradient-to-r from-transparent via-white/[0.06] to-transparent mb-5 sm:mb-7"></div>

        {/* Bottom Bar */}
        <div className="flex flex-col items-center gap-3 sm:gap-4 sm:flex-row sm:justify-between">
          <p className="text-white/20 text-[10px] sm:text-xs tracking-wide font-light text-center sm:text-left">
            © {new Date().getFullYear()} Maison Daliah Secret. Tous droits réservés.
          </p>
          <div className="flex flex-wrap justify-center sm:justify-end items-center gap-3 sm:gap-5 text-[10px] sm:text-xs">
            <Link to="/shipping-returns" className="text-white/20 hover:text-[#D4A574] transition-colors duration-300 cursor-pointer whitespace-nowrap font-light">
              Confidentialité
            </Link>
            <span className="w-[3px] h-[3px] rounded-full bg-[#D4A574]/20"></span>
            <Link to="/shipping-returns" className="text-white/20 hover:text-[#D4A574] transition-colors duration-300 cursor-pointer whitespace-nowrap font-light">
              Conditions
            </Link>
            <span className="w-[3px] h-[3px] rounded-full bg-[#D4A574]/20"></span>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/20 hover:text-[#D4A574] transition-colors duration-300 cursor-pointer whitespace-nowrap font-light">
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
